import { Controller } from "@hotwired/stimulus";

/**
 * Plays a short video clip on hover for video model cards.
 * Skips activation on touch devices.
 *
 * Data attributes:
 *   data-video-preview-src-value — URL to the preview clip (MP4)
 */
export default class VideoPreviewController extends Controller {
  static values = { src: String };
  static targets = ["video"];

  declare srcValue: string;
  declare videoTarget: HTMLVideoElement;

  private _touch = false;

  connect(): void {
    this._touch = window.matchMedia("(hover: none)").matches;
    // src is assigned lazily on first hover in play() so the page doesn't fire
    // a metadata range request for every video card at load. play()'s assign is
    // self-healing, so hover still works on the first mouseenter.
  }

  play(): void {
    if (this._touch) return;

    const video = this.videoTarget;
    // Defensive: if connect() raced the target, assign src now.
    if (this.srcValue && !video.src) video.src = this.srcValue;

    this.element.classList.remove("opacity-0");
    this.element.classList.add("opacity-100");

    try {
      video.currentTime = 0;
    } catch {
      // currentTime can throw before metadata is ready; ignore.
    }
    // play() returns a promise that rejects if interrupted by a quick
    // mouseleave/pause. Swallow it so it never surfaces as an error.
    void video.play().catch(() => {});
  }

  stop(): void {
    this.videoTarget.pause();

    this.element.classList.remove("opacity-100");
    this.element.classList.add("opacity-0");
  }
}
